import React from "react";
import {
  Card,
  Elevation,
  Tag,
  Button,
  H1,
  H3,
  Text,
  InputGroup,
  Intent,
  Tooltip,
  Position,
  NonIdealState,
  Spinner,
  ControlGroup,
  Switch,
  TextArea,
  Alert,
} from "@blueprintjs/core";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine-dark.css";
import { LicenseManager } from "ag-grid-enterprise";
import "ag-grid-enterprise";
import { inject, observer } from "mobx-react";
import { apiCallPost } from "../../../micro/fns/util";
import cogoToast from "cogo-toast";
import BranchService from "../../../micro/services/BranchService";
import AuditService from "../../../micro/services/AuditService";
import NewMultiSelector from "../../../micro/components/NewMultiSelector";
import AuditLogViewer from "../../../micro/components/AuditLogViewer";

// Set AG Grid license
LicenseManager.setLicenseKey("");

class InsuranceCompanyMaster extends React.Component {
  state = {
    // Data
    companies: [],
    branches: [],

    // Loading states
    isLoading: true,
    isSaving: false,

    // Grid state
    gridApi: null,
    columnApi: null,
    quickFilterText: "",

    // Form state for adding new insurance company
    newCompany: {
      companyName: "",
      companyCode: "",
      contactPerson: "",
      contactPhone: "",
      contactEmail: "",
      address: "",
      isActive: true,
      branches: [],
      remarks: "",
    },

    // UI state for new modal
    showNewCompanyModal: false,

    // Edit modal state
    isEditModalOpen: false,
    editingCompany: null,

    // Audit log state
    showAuditLogs: false,

    // Delete confirmation state
    showDeleteConfirmation: false,
    companyToDelete: null,
  };

  componentDidMount() {
    this.loadData();
  }

  onGridReady = (params) => {
    this.setState({ gridApi: params.api, columnApi: params.columnApi });
  };

  columnDefs = [
    {
      headerName: "Company Name",
      field: "companyName",
      width: 220,
      filter: "agTextColumnFilter",
      pinned: "left",
      editable: false,
    },
    {
      headerName: "Company Code",
      field: "companyCode",
      width: 140,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Contact Person",
      field: "contactPerson",
      width: 160,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Contact Phone",
      field: "contactPhone",
      width: 150,
      filter: "agTextColumnFilter",
      editable: false,
    },
    {
      headerName: "Status",
      field: "isActive",
      width: 120,
      filter: "agSetColumnFilter",
      cellRenderer: (params) => (params.value ? "Active" : "Inactive"),
      editable: false,
    },
    {
      headerName: "Branches",
      field: "branches",
      width: 240,
      filter: "agTextColumnFilter",
      cellRenderer: (params) => {
        if (!params.value || params.value.length === 0) {
          return "No branches";
        }

        if (params.value.length <= 2) {
          return params.value.join(", ");
        }

        return params.value.slice(0, 2).join(", ") + ` (+${params.value.length - 2} more)`;
      },
      editable: false,
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      cellRenderer: (params) => {
        const container = document.createElement("div");
        const editBtn = document.createElement("button");
        const deleteBtn = document.createElement("button");

        editBtn.innerHTML = "✏️";
        editBtn.style.marginRight = "5px";
        editBtn.style.padding = "4px 8px";
        editBtn.style.border = "1px solid #137cbd";
        editBtn.style.backgroundColor = "#137cbd";
        editBtn.style.color = "white";
        editBtn.style.borderRadius = "3px";
        editBtn.style.cursor = "pointer";
        editBtn.onclick = () => this.editCompany(params.data);

        deleteBtn.innerHTML = "🗑️";
        deleteBtn.style.padding = "4px 8px";
        deleteBtn.style.border = "1px solid #db3737";
        deleteBtn.style.backgroundColor = "#db3737";
        deleteBtn.style.color = "white";
        deleteBtn.style.borderRadius = "3px";
        deleteBtn.style.cursor = "pointer";
        deleteBtn.onclick = () => this.showDeleteConfirmation(params.data);

        container.appendChild(editBtn);
        container.appendChild(deleteBtn);
        return container;
      },
      filter: false,
      sortable: false,
    },
  ];

  loadData = async () => {
    try {
      this.setState({ isLoading: true });
      cogoToast.loading("Loading insurance companies...");

      await Promise.all([this.loadCompanies(), this.loadBranches()]);

      cogoToast.success("Data loaded successfully");
    } catch (err) {
      cogoToast.error("Failed to load data. Please try again.");
      console.error("Detailed error loading data:", err);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  loadBranches = async () => {
    try {
      const branchNames = await BranchService.getBranchNames(this.props.authStore.prefix);
      this.setState({ branches: branchNames });
    } catch (err) {
      console.error("Failed to load branches:", err);
      this.setState({
        branches: ["Newtown Branch", "Park Street Branch", "Salt Lake Branch", "Howrah Branch"],
      });
    }
  };

  loadCompanies = async () => {
    try {
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxInsuranceCompanyMaster/getAll`, {});
      this.setState({ companies: response || [] });
    } catch (err) {
      console.error("Failed to load insurance companies:", err);
      // fallback: empty list
      this.setState({ companies: [] });
    }
  };

  editCompany = (company) => {
    this.setState({ isEditModalOpen: true, editingCompany: { ...company } });
  };

  updateCompanyFromModal = async () => {
    const { editingCompany } = this.state;

    if (!editingCompany.companyName.trim()) {
      cogoToast.error("Company Name is required");
      return;
    }
    if (!editingCompany.companyCode.trim()) {
      cogoToast.error("Company Code is required");
      return;
    }
    if (!editingCompany.branches.length) {
      cogoToast.error("At least one branch must be selected");
      return;
    }

    const isDuplicateName = this.state.companies.some((existing) => {
      if (existing._id === editingCompany._id) return false;
      return existing.companyName === editingCompany.companyName.trim();
    });
    const isDuplicateCode = this.state.companies.some((existing) => {
      if (existing._id === editingCompany._id) return false;
      return existing.companyCode === editingCompany.companyCode.trim();
    });
    if (isDuplicateName) {
      cogoToast.error(`Company name "${editingCompany.companyName.trim()}" already exists.`);
      return;
    }
    if (isDuplicateCode) {
      cogoToast.error(`Company code "${editingCompany.companyCode.trim()}" already exists.`);
      return;
    }

    try {
      cogoToast.loading("Updating insurance company...");
      this.setState({ isSaving: true });

      const payload = {
        _id: editingCompany._id,
        companyName: editingCompany.companyName.trim(),
        companyCode: editingCompany.companyCode.trim(),
        contactPerson: (editingCompany.contactPerson || "").trim(),
        contactPhone: (editingCompany.contactPhone || "").trim(),
        contactEmail: (editingCompany.contactEmail || "").trim(),
        address: (editingCompany.address || "").trim(),
        isActive: !!editingCompany.isActive,
        branches: editingCompany.branches,
        remarks: (editingCompany.remarks || "").trim(),
      };

      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxInsuranceCompanyMaster/update`,
        payload
      );

      if (response && response._id) {
        const updated = { ...response };
        const oldData = this.state.companies.find((c) => c._id === editingCompany._id) || null;

        this.setState((prev) => ({
          companies: (prev.companies || []).map((c) => (c._id === editingCompany._id ? updated : c)),
          isSaving: false,
          isEditModalOpen: false,
          editingCompany: null,
        }));

        try {
          await AuditService.logEntityUpdate({
            entityType: "insuranceCompany",
            entityId: editingCompany._id,
            oldData,
            newData: updated,
            authStore: this.props.authStore,
            description: `Updated insurance company "${updated.companyName}"`,
          });
        } catch (auditError) {
          console.error("Failed to log audit for update", auditError);
        }

        cogoToast.success("Insurance company updated successfully!");
      } else {
        throw new Error("Invalid response from update API");
      }
    } catch (err) {
      console.error("Error updating insurance company:", err);
      cogoToast.error("Failed to update insurance company. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  closeEditModal = () => {
    this.setState({ isEditModalOpen: false, editingCompany: null });
  };

  handleNewCompanySubmit = () => {
    const { newCompany } = this.state;
    if (!newCompany.companyName.trim()) {
      cogoToast.error("Please enter a company name");
      return;
    }
    if (!newCompany.branches.length) {
      cogoToast.error("Please select at least one branch");
      return;
    }

    const isDuplicateName = this.state.companies.some(
      (existing) => existing.companyName === newCompany.companyName.trim()
    );
    const trimmedNewCode = newCompany.companyCode.trim();
    const isDuplicateCode = trimmedNewCode
      ? this.state.companies.some((existing) => (existing.companyCode || "").trim() === trimmedNewCode)
      : false;
    if (isDuplicateName) {
      cogoToast.error(`Company name "${newCompany.companyName.trim()}" already exists.`);
      return;
    }
    if (isDuplicateCode) {
      cogoToast.error(`Company code "${trimmedNewCode}" already exists.`);
      return;
    }

    this.createNew();
  };

  createNew = async () => {
    this.setState({ isSaving: true });
    cogoToast.loading("Creating new Insurance Company");

    try {
      const payload = {
        companyName: this.state.newCompany.companyName.trim(),
        companyCode: this.state.newCompany.companyCode.trim(),
        contactPerson: (this.state.newCompany.contactPerson || "").trim(),
        contactPhone: (this.state.newCompany.contactPhone || "").trim(),
        contactEmail: (this.state.newCompany.contactEmail || "").trim(),
        address: (this.state.newCompany.address || "").trim(),
        isActive: !!this.state.newCompany.isActive,
        branches: this.state.newCompany.branches,
        remarks: (this.state.newCompany.remarks || "").trim(),
      };

      const response = await apiCallPost(
        `/v3/${this.props.authStore.prefix}/neoSxInsuranceCompanyMaster/create`,
        payload
      );

      if (response && response._id) {
        const created = { ...response };

        try {
          await AuditService.logEntityCreate({
            entityType: "insuranceCompany",
            entityId: created._id,
            newData: created,
            authStore: this.props.authStore,
            description: `Created insurance company "${created.companyName || "Unnamed"}"`,
          });
        } catch (auditError) {
          console.error("Failed to log audit for create", auditError);
        }

        this.setState({
          companies: [created, ...this.state.companies],
          isSaving: false,
          showNewCompanyModal: false,
          newCompany: {
            companyName: "",
            companyCode: "",
            contactPerson: "",
            contactPhone: "",
            contactEmail: "",
            address: "",
            isActive: true,
            branches: [],
            remarks: "",
          },
        });
        cogoToast.success("Insurance company created successfully!");
      } else {
        throw new Error("Invalid response from create API");
      }
    } catch (err) {
      console.error("Error creating insurance company:", err);
      cogoToast.error("Failed to create new insurance company. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  showDeleteConfirmation = (company) => {
    this.setState({
      showDeleteConfirmation: true,
      companyToDelete: company,
    });
  };

  hideDeleteConfirmation = () => {
    this.setState({
      showDeleteConfirmation: false,
      companyToDelete: null,
    });
  };

  confirmDelete = () => {
    if (this.state.companyToDelete) {
      this.deleteCompany(this.state.companyToDelete._id);
      this.hideDeleteConfirmation();
    }
  };

  deleteCompany = async (_id) => {
    this.setState({ isSaving: true });
    cogoToast.loading("Deleting insurance company...");

    const companyToDelete = this.state.companies.find((c) => c._id === _id);

    try {
      const response = await apiCallPost(`/v3/${this.props.authStore.prefix}/neoSxInsuranceCompanyMaster/delete`, {
        _id,
      });

      if (response && response.success !== false) {
        this.setState((prev) => ({
          companies: (prev.companies || []).filter((c) => c._id !== _id),
          isSaving: false,
        }));

        if (companyToDelete) {
          try {
            await AuditService.logEntityDelete({
              entityType: "insuranceCompany",
              entityId: _id,
              deletedData: companyToDelete,
              authStore: this.props.authStore,
              description: `Deleted insurance company "${companyToDelete.companyName}"`,
            });
          } catch (auditError) {
            console.error("Failed to log audit for delete", auditError);
          }
        }

        cogoToast.success("Insurance company deleted successfully!");
      } else {
        throw new Error("Delete operation failed or was rejected");
      }
    } catch (err) {
      console.error("Error deleting insurance company:", err);
      cogoToast.error("Failed to delete insurance company. Please try again.");
      this.setState({ isSaving: false });
    }
  };

  handleShowAuditLogs = () => {
    this.setState({ showAuditLogs: true });
  };

  handleCloseAuditLogs = () => {
    this.setState({ showAuditLogs: false });
  };

  renderHeader = () => {
    const { quickFilterText, isSaving, companies, branches } = this.state;

    return (
      <div style={{ marginBottom: "var(--v3-spacing-lg)" }}>
        <H1 style={{ color: "var(--text-primary)", margin: 0 }}>Insurance Company Master</H1>
        <Text
          style={{
            color: "var(--text-secondary)",
            margin: "var(--v3-spacing-xs) 0",
            fontSize: "var(--v3-font-size-md)",
            fontFamily: "var(--v3-font-family-sans)",
          }}
        >
          Manage insurance companies and their contact and branch configurations
        </Text>

        <Card elevation={Elevation.TWO} style={{ padding: "var(--v3-spacing-lg)", marginTop: "var(--v3-spacing-lg)" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "var(--v3-spacing-md)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1 }}>
              <div style={{ minWidth: "300px" }}>
                <InputGroup
                  leftIcon="search"
                  placeholder="Search companies, codes, or contact details..."
                  value={quickFilterText}
                  onChange={(e) => this.setState({ quickFilterText: e.target.value })}
                  disabled={isSaving}
                />
              </div>

              <div style={{ display: "flex", gap: "8px" }}>
                <Tag large intent="primary" icon="numerical">
                  {companies.length} Companies
                </Tag>
                <Tag large intent="success" icon="map-marker">
                  {branches.length} Branches
                </Tag>
                <Tag large intent="warning" icon="tick-circle">
                  {companies.filter((c) => c.isActive).length} Active
                </Tag>
              </div>
            </div>

            <div style={{ display: "flex", gap: "8px" }}>
              <Tooltip content="Refresh data" position={Position.BOTTOM}>
                <Button
                  icon="refresh"
                  disabled={isSaving}
                  onClick={() => {
                    this.loadBranches();
                    this.loadCompanies();
                  }}
                />
              </Tooltip>

              <Tooltip content="View audit logs" position={Position.BOTTOM}>
                <Button icon="history" disabled={isSaving} onClick={this.handleShowAuditLogs} />
              </Tooltip>

              <Button
                icon="plus"
                intent="primary"
                disabled={isSaving}
                onClick={() => this.setState({ showNewCompanyModal: true })}
              >
                New Company
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  renderTable = () => {
    const { companies, quickFilterText, isLoading } = this.state;

    if (!companies.length) {
      return (
        <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
          <div style={{ textAlign: "center", padding: "40px" }}>
            <H3 style={{ color: "var(--text-primary)", marginBottom: "20px" }}>
              {quickFilterText ? "No matching companies found" : "No insurance companies configured"}
            </H3>
            <Text
              style={{
                color: "var(--text-secondary)",
                marginBottom: "var(--v3-spacing-xl)",
                fontSize: "var(--v3-font-size-md)",
                fontFamily: "var(--v3-font-family-sans)",
              }}
            >
              {quickFilterText
                ? "Try adjusting your search terms or clear the search to view all companies."
                : "Get started by creating your first insurance company record."}
            </Text>
            {quickFilterText ? (
              <Button icon="cross" intent="primary" onClick={() => this.setState({ quickFilterText: "" })}>
                Clear Search
              </Button>
            ) : (
              <Button
                icon="plus"
                intent="primary"
                large
                onClick={() => this.setState({ showNewCompanyModal: true })}
                loading={this.state.isSaving}
              >
                Create First Company
              </Button>
            )}
          </div>
        </Card>
      );
    }

    return (
      <Card elevation={Elevation.TWO} style={{ padding: "20px" }}>
        <div className="ag-theme-alpine-dark" style={{ height: "600px", width: "100%" }}>
          <AgGridReact
            columnDefs={this.columnDefs}
            rowData={companies}
            onGridReady={this.onGridReady}
            quickFilterText={quickFilterText}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
            }}
            rowSelection="single"
            enableRangeSelection={true}
            enableCharts={true}
            sideBar={true}
            statusBar={{
              statusPanels: [
                {
                  statusPanel: "agTotalAndFilteredRowCountComponent",
                  align: "left",
                },
              ],
            }}
            loading={isLoading}
          />
        </div>
      </Card>
    );
  };

  render() {
    if (this.state.showAuditLogs) {
      return <AuditLogViewer onClose={this.handleCloseAuditLogs} />;
    }

    if (this.state.isLoading) {
      return (
        <div
          className="bp3-dark"
          style={{
            padding: "20px",
            backgroundColor: "var(--bg-primary)",
            color: "var(--text-primary)",
            minHeight: "100vh",
          }}
        >
          <NonIdealState
            icon={<Spinner size={50} />}
            title="Loading Insurance Company Masters"
            description="Please wait while we load your insurance companies and branch configurations..."
          />
        </div>
      );
    }

    return (
      <div
        className="bp3-dark"
        style={{
          padding: "20px",
          backgroundColor: "var(--bg-primary)",
          color: "var(--text-primary)",
          minHeight: "100vh",
        }}
      >
        {this.renderHeader()}
        {this.renderTable()}
        {this.renderNewCompanyModal()}
        {this.renderEditModal()}
        {this.renderDeleteConfirmation()}
      </div>
    );
  }

  renderEditModal = () => {
    const { isEditModalOpen, editingCompany, branches, isSaving } = this.state;

    if (!isEditModalOpen || !editingCompany) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={this.closeEditModal}
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "900px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Edit Insurance Company</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Update the insurance company information and branch assignments.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Company Name *
              </Tag>
              <InputGroup
                placeholder="Enter company name..."
                value={editingCompany.companyName}
                onChange={(e) => this.setState({ editingCompany: { ...editingCompany, companyName: e.target.value } })}
                disabled={isSaving}
                intent={!editingCompany.companyName.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "260px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Company Code
              </Tag>
              <InputGroup
                placeholder="Enter company code..."
                value={editingCompany.companyCode}
                onChange={(e) => this.setState({ editingCompany: { ...editingCompany, companyCode: e.target.value } })}
                disabled={isSaving}
                intent={Intent.NONE}
                style={{ width: "160px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Status
              </Tag>
              <Switch
                checked={editingCompany.isActive}
                onChange={(e) => this.setState({ editingCompany: { ...editingCompany, isActive: e.target.checked } })}
                disabled={isSaving}
                label={editingCompany.isActive ? "Active" : "Inactive"}
                style={{ margin: "8px 0" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Person
              </Tag>
              <InputGroup
                placeholder="Enter contact person name..."
                value={editingCompany.contactPerson}
                onChange={(e) =>
                  this.setState({ editingCompany: { ...editingCompany, contactPerson: e.target.value } })
                }
                disabled={isSaving}
                style={{ width: "220px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Phone
              </Tag>
              <InputGroup
                placeholder="Enter phone number..."
                value={editingCompany.contactPhone}
                onChange={(e) => this.setState({ editingCompany: { ...editingCompany, contactPhone: e.target.value } })}
                disabled={isSaving}
                style={{ width: "190px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Email
              </Tag>
              <InputGroup
                placeholder="Enter email address..."
                value={editingCompany.contactEmail}
                onChange={(e) => this.setState({ editingCompany: { ...editingCompany, contactEmail: e.target.value } })}
                disabled={isSaving}
                style={{ width: "260px" }}
              />
            </ControlGroup>

            <div style={{ width: "100%" }}>
              <ControlGroup>
                <Tag large minimal>
                  Address
                </Tag>
                <TextArea
                  placeholder="Enter company address..."
                  value={editingCompany.address}
                  onChange={(e) => this.setState({ editingCompany: { ...editingCompany, address: e.target.value } })}
                  disabled={isSaving}
                  style={{ width: "100%", minHeight: "60px" }}
                />
              </ControlGroup>
            </div>

            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={editingCompany.branches}
                    onChange={(selected) =>
                      this.setState({ editingCompany: { ...editingCompany, branches: selected } })
                    }
                    placeholder="Select branches for this company..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>

            <div style={{ width: "100%" }}>
              <ControlGroup>
                <Tag large minimal>
                  Remarks
                </Tag>
                <TextArea
                  placeholder="Enter any additional remarks..."
                  value={editingCompany.remarks}
                  onChange={(e) => this.setState({ editingCompany: { ...editingCompany, remarks: e.target.value } })}
                  disabled={isSaving}
                  style={{ width: "100%", minHeight: "60px" }}
                />
              </ControlGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button large intent="none" icon="cross" onClick={this.closeEditModal}>
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.updateCompanyFromModal}
              loading={isSaving}
              disabled={isSaving || !editingCompany.companyName.trim() || !editingCompany.branches.length}
            >
              Update Company
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderNewCompanyModal = () => {
    const { showNewCompanyModal, newCompany, branches, isSaving } = this.state;

    if (!showNewCompanyModal) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
        onClick={() =>
          this.setState({
            showNewCompanyModal: false,
            newCompany: {
              companyName: "",
              companyCode: "",
              contactPerson: "",
              contactPhone: "",
              contactEmail: "",
              address: "",
              isActive: true,
              branches: [],
              remarks: "",
            },
          })
        }
      >
        <div
          style={{
            backgroundColor: "var(--bg-surface)",
            borderRadius: "8px",
            padding: "30px",
            maxWidth: "900px",
            width: "90%",
            maxHeight: "90vh",
            overflowY: "auto",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ marginBottom: "20px" }}>
            <H3 style={{ color: "var(--text-primary)", margin: 0 }}>Create New Insurance Company</H3>
            <p style={{ color: "var(--text-secondary)", margin: "5px 0" }}>
              Enter the insurance company information and configure service parameters.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "15px", marginBottom: "20px" }}>
            <ControlGroup>
              <Tag large minimal>
                Company Name *
              </Tag>
              <InputGroup
                placeholder="Enter company name..."
                value={newCompany.companyName}
                onChange={(e) => this.setState({ newCompany: { ...newCompany, companyName: e.target.value } })}
                disabled={isSaving}
                intent={!newCompany.companyName.trim() ? Intent.DANGER : Intent.NONE}
                style={{ width: "260px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Company Code
              </Tag>
              <InputGroup
                placeholder="Enter company code..."
                value={newCompany.companyCode}
                onChange={(e) => this.setState({ newCompany: { ...newCompany, companyCode: e.target.value } })}
                disabled={isSaving}
                intent={Intent.NONE}
                style={{ width: "160px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Status
              </Tag>
              <Switch
                checked={newCompany.isActive}
                onChange={(e) => this.setState({ newCompany: { ...newCompany, isActive: e.target.checked } })}
                disabled={isSaving}
                label={newCompany.isActive ? "Active" : "Inactive"}
                style={{ margin: "8px 0" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Person
              </Tag>
              <InputGroup
                placeholder="Enter contact person name..."
                value={newCompany.contactPerson}
                onChange={(e) => this.setState({ newCompany: { ...newCompany, contactPerson: e.target.value } })}
                disabled={isSaving}
                style={{ width: "220px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Phone
              </Tag>
              <InputGroup
                placeholder="Enter phone number..."
                value={newCompany.contactPhone}
                onChange={(e) => this.setState({ newCompany: { ...newCompany, contactPhone: e.target.value } })}
                disabled={isSaving}
                style={{ width: "190px" }}
              />
            </ControlGroup>

            <ControlGroup>
              <Tag large minimal>
                Contact Email
              </Tag>
              <InputGroup
                placeholder="Enter email address..."
                value={newCompany.contactEmail}
                onChange={(e) => this.setState({ newCompany: { ...newCompany, contactEmail: e.target.value } })}
                disabled={isSaving}
                style={{ width: "260px" }}
              />
            </ControlGroup>

            <div style={{ width: "100%" }}>
              <ControlGroup>
                <Tag large minimal>
                  Address
                </Tag>
                <TextArea
                  placeholder="Enter company address..."
                  value={newCompany.address}
                  onChange={(e) => this.setState({ newCompany: { ...newCompany, address: e.target.value } })}
                  disabled={isSaving}
                  style={{ width: "100%", minHeight: "60px" }}
                />
              </ControlGroup>
            </div>

            <div style={{ width: "100%", marginTop: "10px" }}>
              <ControlGroup>
                <Tag large minimal>
                  Branches *
                </Tag>
                <div style={{ flex: 1, minWidth: "400px" }}>
                  <NewMultiSelector
                    items={branches}
                    selectedItems={newCompany.branches}
                    onChange={(selected) => this.setState({ newCompany: { ...newCompany, branches: selected } })}
                    placeholder="Select branches for this company..."
                    label="Branches"
                    showLabel={false}
                    maxDisplayItems={3}
                    sortItems={true}
                    emptyMessage="No branches available"
                    disabled={isSaving}
                  />
                </div>
              </ControlGroup>
            </div>

            <div style={{ width: "100%" }}>
              <ControlGroup>
                <Tag large minimal>
                  Remarks
                </Tag>
                <TextArea
                  placeholder="Enter any additional remarks..."
                  value={newCompany.remarks}
                  onChange={(e) => this.setState({ newCompany: { ...newCompany, remarks: e.target.value } })}
                  disabled={isSaving}
                  style={{ width: "100%", minHeight: "60px" }}
                />
              </ControlGroup>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button
              large
              intent="none"
              icon="cross"
              onClick={() =>
                this.setState({
                  showNewCompanyModal: false,
                  newCompany: {
                    companyName: "",
                    companyCode: "",
                    contactPerson: "",
                    contactPhone: "",
                    contactEmail: "",
                    address: "",
                    isActive: true,
                    branches: [],
                    remarks: "",
                  },
                })
              }
            >
              Cancel
            </Button>
            <Button
              large
              intent="primary"
              icon="tick"
              onClick={this.handleNewCompanySubmit}
              loading={isSaving}
              disabled={isSaving || !newCompany.companyName.trim() || !newCompany.branches.length}
            >
              Create Company
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderDeleteConfirmation = () => {
    const { showDeleteConfirmation, companyToDelete } = this.state;

    return (
      <Alert
        isOpen={showDeleteConfirmation}
        intent={Intent.DANGER}
        icon="trash"
        onCancel={this.hideDeleteConfirmation}
        onConfirm={this.confirmDelete}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
      >
        <p>
          Are you sure you want to delete the insurance company <strong>"{companyToDelete?.companyName}"</strong>?
        </p>
        <p>This action cannot be undone and will permanently remove this insurance company from the system.</p>
        {companyToDelete && (
          <div style={{ marginTop: "12px", fontSize: "14px", color: "var(--text-secondary)" }}>
            <strong>Company details:</strong>
            <div style={{ marginTop: "4px" }}>Code: {companyToDelete.companyCode || "N/A"}</div>
            <div>Contact: {companyToDelete.contactPerson || "N/A"}</div>
            <div>Status: {companyToDelete.isActive ? "Active" : "Inactive"}</div>
            {companyToDelete.branches && companyToDelete.branches.length > 0 && (
              <div>Branches: {companyToDelete.branches.join(", ")}</div>
            )}
          </div>
        )}
      </Alert>
    );
  };
}

export default inject("authStore")(observer(InsuranceCompanyMaster));
